// FACILITY_REGISTRY — single source of truth for every constructible
// facility: id, category, level track (cost / days / upkeep / modifiers /
// caption / asset tier) and unlock requirements. Adding a facility means
// adding ONE entry here (plus optional art): the UI, construction queue,
// economy and demand all derive from this registry.
//
// Compatibility: `Building.type` was always persisted as a plain string, so
// widening the catalog needs no save migration. The four legacy buildings
// keep their exact ids, level 1-3 costs and flat upkeep so old saves are
// numerically identical after the refactor.

import type { Modifier } from '@/game/systems/ModifierSystem'

export type FacilityCategory =
  | 'core'         // reception, locker
  | 'hospitality'  // cafe, restaurant, kids zone
  | 'commerce'     // shop, parking
  | 'performance'  // gym, physio, academy (Fase 2+)
  | 'wellness'     // pool, spa
  | 'prestige'     // VIP box, press room, solar (Fase 3+)

export interface FacilityRequirement {
  /** Minimum club level (0-9) */
  clubLevel?: number
  /** Minimum league division, 4 (Cuarta) … 1 (Primera). Enforced in Fase 2
   *  as the "Licencia Federativa" hard gate. */
  leagueDivision?: number
  /** Minimum club reputation (0-5) */
  reputation?: number
}

export interface FacilityLevel {
  level: number
  /** Level 1 = build cost; level N = cost of the upgrade to N */
  cost: number
  constructionDays: number
  maintenancePerWeek: number
  /** Which campus illustration tier represents this level (several levels
   *  can share one image; `getFacilityAsset` picks closest-at-or-below). */
  assetTier: number
  caption: string
  /** ABSOLUTE modifier set at this level (not deltas) — makes the "next
   *  level" diff trivial to render and collection stateless. */
  modifiers: Modifier[]
  /** Soft cap: requirements to reach THIS level (beyond building at all) */
  requirements?: FacilityRequirement
}

/** Terminal specialization: at the track's max level the club picks ONE of
 *  two identities for the facility — a permanent, exclusive bonus. */
export interface FacilitySpec {
  id: string
  name: string
  emoji: string
  description: string
  modifiers: Modifier[]
}

export interface FacilityDefinition {
  id: string // plain string on purpose — NOT a closed union
  name: string
  emoji: string
  category: FacilityCategory
  description: string
  /** Two gradient stops for the placeholder card while art is missing */
  placeholderGradient: [string, string]
  /** Requirements to build at all (level 1) */
  requirements: FacilityRequirement
  levels: FacilityLevel[]
  /** Exactly two mutually exclusive branches, offered at max level */
  specs?: [FacilitySpec, FacilitySpec]
}

// --- Level track builders ---------------------------------------------------

/** Extend a seed cost array with a ×growth curve, rounded to 100s. */
function extendCosts(seed: number[], totalLevels: number, growth = 1.6): number[] {
  const costs = [...seed]
  while (costs.length < totalLevels) {
    costs.push(Math.round((costs[costs.length - 1] * growth) / 100) * 100)
  }
  return costs
}

/** Default soft cap ladder: levels 1-3 free, then club-level gates, and from
 *  level 5 the Licencia Federativa (minimum league division) kicks in — the
 *  hard half of the club↔division coherence design. */
function defaultLevelCap(level: number): FacilityRequirement | undefined {
  const caps: Record<number, FacilityRequirement> = {
    4: { clubLevel: 4 },
    5: { clubLevel: 4, leagueDivision: 3 },
    6: { clubLevel: 5, leagueDivision: 3 },
    7: { clubLevel: 6, leagueDivision: 2 },
    8: { clubLevel: 7, leagueDivision: 2 },
    9: { clubLevel: 8, leagueDivision: 1 },
    10: { clubLevel: 9, leagueDivision: 1 },
  }
  return caps[level]
}

interface TrackSpec {
  costs: number[]
  buildDays: number
  /** Weekly upkeep per level (1-indexed by position) */
  maintenance: (level: number) => number
  captions: string[]
  maxAssetTier: number
  modifiers: (level: number) => Modifier[]
}

function buildTrack(spec: TrackSpec): FacilityLevel[] {
  return spec.costs.map((cost, i) => {
    const level = i + 1
    return {
      level,
      cost,
      constructionDays: level === 1 ? spec.buildDays : 2 + Math.floor(level / 2),
      maintenancePerWeek: Math.round(spec.maintenance(level)),
      assetTier: Math.min(level, spec.maxAssetTier),
      caption: spec.captions[i] ?? `Nivel ${level}`,
      modifiers: spec.modifiers(level),
      requirements: defaultLevelCap(level),
    }
  })
}

/** Legacy upkeep: flat through level 3 (exact parity with old saves), then
 *  +35% of the base per extra level. */
const legacyMaintenance = (base: number) => (level: number) =>
  level <= 3 ? base : base * (1 + 0.35 * (level - 3))

/** New facilities scale upkeep from level 1. */
const scaledMaintenance = (base: number) => (level: number) =>
  base * (1 + 0.35 * (level - 1))

/** Legacy demand contribution: exactly the old `0.03 × level` so migrated
 *  saves produce identical demand numbers. */
const demandPerLevel = (level: number): Modifier =>
  ({ stat: 'demand.all', op: 'add', value: 0.03 * level })

// --- The registry -----------------------------------------------------------

export const FACILITY_REGISTRY: Record<string, FacilityDefinition> = {
  reception: {
    id: 'reception',
    name: 'Recepción',
    emoji: '🏢',
    category: 'core',
    description: 'Centro de operaciones. Mejora la satisfacción del cliente.',
    placeholderGradient: ['#1E3A5F', '#4ADE80'],
    requirements: { clubLevel: 1 },
    levels: buildTrack({
      // L1 build 3000; upgrades 2000, 5000 (legacy-exact), then ×1.6
      costs: extendCosts([3000, 2000, 5000], 10),
      buildDays: 4,
      maintenance: legacyMaintenance(100),
      captions: [
        'Mostrador sencillo', 'Recepción moderna', 'Lobby premium',
        'Recepción con sala de espera', 'Atención personalizada VIP',
        'Hall de doble altura', 'Recepción inteligente',
        'Centro de atención integral', 'Lobby de club nacional', 'Recepción icónica',
      ],
      maxAssetTier: 3,
      modifiers: level => [
        demandPerLevel(level),
        { stat: 'satisfaction', op: 'add', value: 2 * level },
      ],
    }),
    specs: [
      {
        id: 'concierge',
        name: 'Concierge',
        emoji: '🛎️',
        description: 'Atención personalizada de hotel de cinco estrellas.',
        modifiers: [{ stat: 'satisfaction', op: 'add', value: 12 }],
      },
      {
        id: 'comercial',
        name: 'Recepción Comercial',
        emoji: '💼',
        description: 'El mostrador vende: reservas premium y venta cruzada.',
        modifiers: [{ stat: 'income.retail', op: 'mult', value: 1.1 }],
      },
    ],
  },

  shop: {
    id: 'shop',
    name: 'Pro Shop',
    emoji: '🏪',
    category: 'commerce',
    description: 'Tienda de equipamiento. Genera ingresos adicionales.',
    placeholderGradient: ['#1E3A5F', '#FB923C'],
    requirements: { clubLevel: 2 },
    levels: buildTrack({
      costs: extendCosts([4000, 3000, 6000], 10),
      buildDays: 4,
      maintenance: legacyMaintenance(80),
      captions: [
        'Estantería de palas', 'Tienda equipada', 'Boutique deportiva',
        'Tienda con probadores', 'Boutique especializada',
        'Córner de marcas premium', 'Centro de encordado y custom',
        'Flagship store', 'Tienda referente nacional', 'Megastore del pádel',
      ],
      maxAssetTier: 3,
      modifiers: level => [
        demandPerLevel(level),
        // Retail boost is NEW content: starts at level 4 so legacy saves
        // (max level 3) keep their exact retail income.
        ...(level >= 4
          ? [{ stat: 'income.retail', op: 'mult', value: 1 + 0.05 * (level - 3) } as Modifier]
          : []),
      ],
    }),
    specs: [
      {
        id: 'marca_propia',
        name: 'Marca Propia',
        emoji: '🏷️',
        description: 'Línea de palas y textil del club: margen máximo.',
        modifiers: [{ stat: 'income.retail', op: 'mult', value: 1.2 }],
      },
      {
        id: 'outlet',
        name: 'Outlet del Pádel',
        emoji: '🛍️',
        description: 'Precios imbatibles que atraen público de toda la zona.',
        modifiers: [{ stat: 'demand.all', op: 'add', value: 0.06 }],
      },
    ],
  },

  locker: {
    id: 'locker',
    name: 'Vestuarios',
    emoji: '🚿',
    category: 'core',
    description: 'Vestuarios con duchas. Aumenta la satisfacción y la reputación.',
    placeholderGradient: ['#1E3A5F', '#94A3B8'],
    requirements: { clubLevel: 2 },
    levels: buildTrack({
      // Legacy track stopped at level 2 — level 3+ is new headroom
      costs: extendCosts([5000, 4000, 7000], 10),
      buildDays: 4,
      maintenance: legacyMaintenance(120),
      captions: [
        'Vestuarios funcionales', 'Vestuarios con spa', 'Vestuarios amplios',
        'Vestuarios con sauna', 'Zona wellness', 'Vestuarios premium',
        'Spa deportivo', 'Centro de recuperación', 'Vestuarios de élite',
        'Santuario wellness',
      ],
      maxAssetTier: 2,
      modifiers: level => [
        demandPerLevel(level),
        { stat: 'satisfaction', op: 'add', value: 3 * level },
      ],
    }),
    specs: [
      {
        id: 'spa_premium',
        name: 'Spa Premium',
        emoji: '🧖',
        description: 'Circuito de aguas y tratamientos: los socios salen flotando.',
        modifiers: [{ stat: 'satisfaction', op: 'add', value: 15 }],
      },
      {
        id: 'club_social',
        name: 'Club Social',
        emoji: '🥂',
        description: 'El vestuario como punto de encuentro: nadie quiere irse.',
        modifiers: [{ stat: 'demand.all', op: 'add', value: 0.06 }],
      },
    ],
  },

  cafe: {
    id: 'cafe',
    name: 'Cafetería',
    emoji: '☕',
    category: 'hospitality',
    description: 'Zona de descanso con bar. Genera ingresos y fideliza.',
    placeholderGradient: ['#1E3A5F', '#F87171'],
    requirements: { clubLevel: 3 },
    levels: buildTrack({
      costs: extendCosts([6000, 4000, 8000], 10),
      buildDays: 4,
      maintenance: legacyMaintenance(150),
      captions: [
        'Barra de café', 'Cafetería con terraza', 'Restaurante del club',
        'Cocina ampliada', 'Terraza panorámica', 'Gastrobar',
        'Restaurante con chef', 'Salón de eventos', 'Restaurante insignia',
        'Club gastronómico',
      ],
      maxAssetTier: 3,
      modifiers: level => [
        demandPerLevel(level),
        { stat: 'satisfaction', op: 'add', value: level },
        ...(level >= 4
          ? [{ stat: 'income.retail', op: 'mult', value: 1 + 0.05 * (level - 3) } as Modifier]
          : []),
      ],
    }),
    specs: [
      {
        id: 'gastro',
        name: 'Club Gastronómico',
        emoji: '👨‍🍳',
        description: 'Cocina de autor: la carta llena tanto como las pistas.',
        modifiers: [{ stat: 'income.retail', op: 'mult', value: 1.15 }],
      },
      {
        id: 'terraza_social',
        name: 'Terraza Social',
        emoji: '🌇',
        description: 'El after-pádel definitivo: ambiente que trae gente.',
        modifiers: [
          { stat: 'demand.all', op: 'add', value: 0.05 },
          { stat: 'satisfaction', op: 'add', value: 5 },
        ],
      },
    ],
  },

  parking: {
    id: 'parking',
    name: 'Parking',
    emoji: '🅿️',
    category: 'commerce',
    description: 'Aparcamiento para socios y visitantes. Atrae más público.',
    placeholderGradient: ['#1E3A5F', '#38BDF8'],
    requirements: { clubLevel: 2 },
    levels: buildTrack({
      costs: [2500, 4000, 6500, 10500, 17000],
      buildDays: 2,
      maintenance: scaledMaintenance(40),
      captions: [
        'Explanada de tierra', 'Parking asfaltado', 'Plazas techadas',
        'Parking con cargadores EV', 'Parking inteligente',
      ],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'demand.all', op: 'add', value: 0.04 + 0.035 * (level - 1) },
        ...(level >= 2
          ? [{ stat: 'satisfaction', op: 'add', value: 2 * (level - 1) } as Modifier]
          : []),
      ],
    }),
  },

  pool: {
    id: 'pool',
    name: 'Piscina',
    emoji: '🏊',
    category: 'wellness',
    description: 'Piscina del club. Un salto de categoría en satisfacción... y en gastos.',
    placeholderGradient: ['#0E7490', '#38BDF8'],
    requirements: { clubLevel: 4, reputation: 3.5 },
    levels: buildTrack({
      costs: [12000, 20000, 34000, 58000, 99000],
      buildDays: 6,
      maintenance: scaledMaintenance(250),
      captions: [
        'Piscina exterior', 'Zona de hamacas', 'Piscina climatizada',
        'Pool bar', 'Oasis del club',
      ],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'satisfaction', op: 'add', value: 5 + 5 * level },
        { stat: 'demand.all', op: 'add', value: 0.05 + 0.025 * (level - 1) },
        ...(level >= 5
          ? [{ stat: 'income.total', op: 'mult', value: 1.05 } as Modifier]
          : []),
      ],
    }),
    specs: [
      {
        id: 'competicion',
        name: 'Piscina de Competición',
        emoji: '🏅',
        description: 'Calles reglamentarias y crono: prestigio deportivo semanal.',
        modifiers: [{ stat: 'reputation.weekly', op: 'add', value: 0.02 }],
      },
      {
        id: 'oasis',
        name: 'Oasis Wellness',
        emoji: '🌴',
        description: 'Hamacas, cócteles y música chill: satisfacción máxima.',
        modifiers: [{ stat: 'satisfaction', op: 'add', value: 15 }],
      },
    ],
  },

  gym: {
    id: 'gym',
    name: 'Gimnasio',
    emoji: '🏋️',
    category: 'performance',
    description: 'Sala de fuerza y preparación física para socios y jugadores.',
    placeholderGradient: ['#1E3A5F', '#A78BFA'],
    requirements: { clubLevel: 4 },
    levels: buildTrack({
      costs: extendCosts([8000, 6000, 10000], 6),
      buildDays: 4,
      maintenance: scaledMaintenance(90),
      captions: [
        'Sala de pesas básica', 'Zona cardio', 'Gimnasio completo',
        'Sala funcional', 'Centro de preparación física', 'Gimnasio de alto rendimiento',
      ],
      maxAssetTier: 1,
      modifiers: level => [
        demandPerLevel(level),
        { stat: 'satisfaction', op: 'add', value: 2 * level },
      ],
    }),
    specs: [
      {
        id: 'alto_rendimiento',
        name: 'Alto Rendimiento',
        emoji: '⚡',
        description: 'Preparación de élite: el nombre del club suena entre profesionales.',
        modifiers: [{ stat: 'reputation.weekly', op: 'add', value: 0.015 }],
      },
      {
        id: 'fitness_socios',
        name: 'Fitness para Socios',
        emoji: '🤸',
        description: 'Clases dirigidas y horario amplio: el gimnasio llena el club.',
        modifiers: [{ stat: 'demand.all', op: 'add', value: 0.06 }],
      },
    ],
  },

  academy: {
    id: 'academy',
    name: 'Academia de Pádel',
    emoji: '🎓',
    category: 'performance',
    description: 'Edificio dedicado a la enseñanza: dispara los ingresos de clases.',
    placeholderGradient: ['#1E3A5F', '#4ADE80'],
    requirements: { clubLevel: 5 },
    levels: buildTrack({
      costs: [15000, 12000, 20000, 32000, 52000],
      buildDays: 5,
      maintenance: scaledMaintenance(150),
      captions: [
        'Aula y pista de enseñanza', 'Programa de tecnificación', 'Academia reconocida',
        'Cantera de promesas', 'Academia de referencia nacional',
      ],
      maxAssetTier: 1,
      modifiers: level => [
        demandPerLevel(level),
        { stat: 'income.class', op: 'mult', value: 1 + 0.06 * level },
      ],
    }),
    specs: [
      {
        id: 'cantera',
        name: 'Cantera',
        emoji: '🌱',
        description: 'Formar campeones desde niños: la región entera te mira.',
        modifiers: [
          { stat: 'reputation.weekly', op: 'add', value: 0.015 },
          { stat: 'demand.all', op: 'add', value: 0.03 },
        ],
      },
      {
        id: 'masterclass',
        name: 'Masterclass',
        emoji: '🎯',
        description: 'Clínics con figuras del circuito: las clases se pagan a precio de oro.',
        modifiers: [{ stat: 'income.class', op: 'mult', value: 1.2 }],
      },
    ],
  },

  kids_zone: {
    id: 'kids_zone',
    name: 'Zona Infantil',
    emoji: '🧒',
    category: 'hospitality',
    description: 'Ludoteca y minipistas: las familias se quedan más tiempo.',
    placeholderGradient: ['#1E3A5F', '#FB923C'],
    requirements: { clubLevel: 3 },
    levels: buildTrack({
      costs: [4000, 3500, 6000, 9500, 15500],
      buildDays: 3,
      maintenance: scaledMaintenance(60),
      captions: [
        'Rincón de juegos', 'Ludoteca vigilada', 'Minipista infantil',
        'Club de verano', 'Paraíso familiar',
      ],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'demand.all', op: 'add', value: 0.04 + 0.03 * (level - 1) },
        { stat: 'satisfaction', op: 'add', value: 2 * level },
      ],
    }),
  },

  vip_box: {
    id: 'vip_box',
    name: 'Palco VIP',
    emoji: '🥂',
    category: 'prestige',
    description: 'Hospitalidad premium sobre la pista central. La élite paga por estar.',
    placeholderGradient: ['#1E3A5F', '#F5C542'],
    requirements: { clubLevel: 7, leagueDivision: 2 },
    levels: buildTrack({
      costs: [30000, 25000, 45000],
      buildDays: 6,
      maintenance: scaledMaintenance(200),
      captions: ['Palco con catering', 'Palco corporativo', 'Sky lounge'],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'income.total', op: 'mult', value: 1 + 0.03 * level },
        { stat: 'satisfaction', op: 'add', value: 3 * level },
      ],
    }),
  },

  press_room: {
    id: 'press_room',
    name: 'Sala de Prensa',
    emoji: '🎙️',
    category: 'prestige',
    description: 'Ruedas de prensa y medios: el club sale en las noticias cada semana.',
    placeholderGradient: ['#1E3A5F', '#94A3B8'],
    requirements: { clubLevel: 6 },
    levels: buildTrack({
      costs: [18000, 14000, 24000],
      buildDays: 4,
      maintenance: scaledMaintenance(110),
      captions: ['Sala de entrevistas', 'Set de televisión', 'Centro de medios'],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'reputation.weekly', op: 'add', value: 0.01 * level },
        demandPerLevel(level),
      ],
    }),
  },

  solar: {
    id: 'solar',
    name: 'Placas Solares',
    emoji: '☀️',
    category: 'prestige',
    description: 'Energía propia: recorta la factura de mantenimiento de todo el club.',
    placeholderGradient: ['#1E3A5F', '#FBBF24'],
    requirements: { clubLevel: 5 },
    levels: buildTrack({
      costs: [16000, 13000, 22000],
      buildDays: 4,
      maintenance: scaledMaintenance(20),
      captions: ['Cubierta solar parcial', 'Instalación ampliada', 'Club autosuficiente'],
      maxAssetTier: 1,
      modifiers: level => [
        { stat: 'expenses.maintenance', op: 'mult', value: 1 - 0.05 * level },
      ],
    }),
  },
}

export const FACILITY_IDS = Object.keys(FACILITY_REGISTRY)

// --- Lookups ----------------------------------------------------------------

export function getFacilityDef(id: string): FacilityDefinition | undefined {
  return FACILITY_REGISTRY[id]
}

export function getFacilityLevel(id: string, level: number): FacilityLevel | undefined {
  return FACILITY_REGISTRY[id]?.levels.find(l => l.level === level)
}

export function getFacilityMaxLevel(id: string): number {
  return FACILITY_REGISTRY[id]?.levels.length ?? 0
}

/** Can the club (level / reputation / league division) meet a requirement
 *  set? Divisions are inverted: 1 (Primera) is best, so current qualifies
 *  when currentDivision <= required. Omit `currentDivision` to skip the
 *  division check (e.g. for the initial-build gate, which never uses it). */
export function meetsRequirements(
  req: FacilityRequirement | undefined,
  clubLevel: number,
  reputation: number,
  currentDivision?: number,
): boolean {
  if (!req) return true
  if (req.clubLevel !== undefined && clubLevel < req.clubLevel) return false
  if (req.reputation !== undefined && reputation < req.reputation) return false
  if (req.leagueDivision !== undefined && currentDivision !== undefined
    && currentDivision > req.leagueDivision) return false
  return true
}

/** Highest level this facility may reach right now: soft-capped by club
 *  level and, from level 5, by league division (Licencia Federativa).
 *  Pass the sentinel NO_LEAGUE_DIVISION (5) when the club has no league.
 *  Reputation gates only the initial build, not later levels. */
export function getMaxAllowedLevel(id: string, clubLevel: number, currentDivision = Infinity): number {
  const def = FACILITY_REGISTRY[id]
  if (!def) return 0
  let max = 0
  for (const level of def.levels) {
    const req = level.requirements
    if (req?.clubLevel !== undefined && clubLevel < req.clubLevel) break
    if (req?.leagueDivision !== undefined && currentDivision > req.leagueDivision) break
    max = level.level
  }
  return max
}

export function getBuildCost(id: string): number {
  return FACILITY_REGISTRY[id]?.levels[0]?.cost ?? 0
}

export function getBuildDays(id: string): number {
  return FACILITY_REGISTRY[id]?.levels[0]?.constructionDays ?? 0
}

export function getFacilitySpec(facilityId: string, specId: string): FacilitySpec | undefined {
  return FACILITY_REGISTRY[facilityId]?.specs?.find(s => s.id === specId)
}
